import { WebuiIocKey } from './WebuiIocKey'

export type ServiceIdentifier = string | symbol

type Binding<T> = { type: 'function-component'; value: T } | { type: 'factory'; factory: (container: Container) => T }

export type BindFunction = <T>(key: ServiceIdentifier) => {
  to(value: T): void
  toFactory(factory: (container: Container) => T): void
}

export class ContainerModule {
  constructor(public readonly registry: (bind: BindFunction) => void) {}
}

/**
 * No-op fallback for `WebuiIocKey.company.companySelect`, bound on every
 * `Container` at construction. `UpdateProfile` (`@ztechapps/webui.auth`) is
 * shared across every admin app and renders a "Company" field unconditionally,
 * but only `panelpunches/admin` loads `@ztechapps/company.webui`'s
 * `companyContainer` (the real `CompanySelect`) — apps with no company
 * concept (QuickScore, OwnText, GateKeeper) would otherwise throw "No
 * binding found for key" on every render of Settings -> Organization ->
 * Users. Mirrors the `ai.chatRenderer.unknown` -> `NoOpChatRenderer`
 * sentinel (`libs/ai/webui/src/container.ts`), generalized to the container
 * itself since, unlike AI chat renderers, `UpdateProfile` is not opt-in per
 * app. `companyContainer`'s bind overwrites this when an app loads it (see
 * `apps/panelpunches/admin/src/config/setup-container.ts`) — later binds win.
 */
const NoOpCompanySelect = () => null

export class Container {
  private bindings = new Map<ServiceIdentifier, Binding<any>>()
  private instances = new Map<ServiceIdentifier, any>()

  constructor() {
    this.bind(WebuiIocKey.company.companySelect).to(NoOpCompanySelect)
  }

  bind: BindFunction = <T>(key: ServiceIdentifier) => {
    return {
      to: (value: T) => {
        this.bindings.set(key, { type: 'function-component', value })
      },
      toFactory: (factory: (container: Container) => T) => {
        this.bindings.set(key, { type: 'factory', factory })
      },
    }
  }

  get<T>(key: ServiceIdentifier): T {
    if (this.instances.has(key)) return this.instances.get(key)

    const binding = this.bindings.get(key)
    if (!binding) throw new Error(`No binding found for key: ${key.toString()}`)

    let instance: T

    switch (binding.type) {
      case 'function-component':
        instance = binding.value
        break
      case 'factory':
        instance = binding.factory(this)
        break
    }

    this.instances.set(key, instance)
    return instance
  }

  load(...modules: ContainerModule[]) {
    for (const module of modules) {
      module.registry(this.bind)
    }
  }
}
